import { createSlice, createAsyncThunk, PayloadAction, Action } from '@reduxjs/toolkit';
import axiosInstance from '../../Services/APIService';
import { RootState } from 'app/store';
import {
  CreateTemplatePayload,
  DeleteTemplateByIdPayload,
  DocumentTemplateState,
  GetTemplateByIdPayload,
  ListTemplatesResponse,
  Template,
  UpdateTemplateByIdPayload,
  GetAllAssignedDocumentsPayload,
  GetAllAssignedDocumentsResponse,
  AssignedDocument,
  SubmitESignDocPayload,
  SubmitESignDocResponse,
  AssignTemplatePayload,
  AssignTemplateResponse,
  AcceptDeleteReassignDocPayload,
  AcceptDeleteReassignDocResponse,
  CrudCategoryPayload,
  CrudCategoryResponse,
  CrudSubcategoryPayload,
  CrudSubcategoryResponse,
  Category,
  Subcategory
} from 'components/payrol/documentManagement/types';

// Enhanced Initial State with subcategory and CRUD functionality
const initialState: DocumentTemplateState = {
  loading: false,
  error: null,
  success: false,
  message: null,
  templates: [],
  categories: [],
  availableCategories: [],
  availableSubcategories: [],
  totalCategories: 0,
  totalSubcategories: 0,
  totalTemplates: 0,
  hasMoreTemplates: true,
  categoryLoading: false,
  categoryError: null,
  subcategoryLoading: false,
  subcategoryError: null,
  templateCreating: false,
  templateFetching: false,
  templateUpdating: false,
  templateDeleting: false,
  templateAssigning: false,
  templateAssignmentError: null,
  templateAssignmentSuccess: false,
  templateAssignmentMessage: null,
  selectedTemplate: null,
  assignedDocumentsLoading: false,
  assignedDocumentsError: null,
  assignedDocumentCategories: [],
  totalAssignedDocumentCategories: 0,
  totalAssignedDocuments: 0,
  selectedAssignedDocument: null,
  acceptDeleteReassignLoading: false,
  acceptDeleteReassignError: null,
  acceptDeleteReassignSuccess: false,
  acceptDeleteReassignMessage: null
};

// Async Thunks

// CRUD Categories
export const crudCategories = createAsyncThunk<
  CrudCategoryResponse,
  CrudCategoryPayload,
  { state: RootState; rejectValue: string }
>('documentTemplate/crudCategories', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/people-screening/doc/crud-categories', payload);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to perform category operation');
  }
});

// CRUD Subcategories
export const crudSubcategories = createAsyncThunk<
  CrudSubcategoryResponse,
  CrudSubcategoryPayload,
  { state: RootState; rejectValue: string }
>('documentTemplate/crudSubcategories', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/people-screening/doc/crud-sub-categories', payload);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to perform subcategory operation'
    );
  }
});

export const acceptDeleteReassignDoc = createAsyncThunk<
  AcceptDeleteReassignDocResponse,
  AcceptDeleteReassignDocPayload,
  { state: RootState; rejectValue: string }
>('documentTemplate/acceptDeleteReassignDoc', async (payload, { rejectWithValue }) => {
  try {
    console.log('Accept/Delete/Reassign Payload:', payload);
    const response = await axiosInstance.post(
      '/people-screening/doc/accept-delete-reassign-doc',
      payload
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to process document action');
  }
});

export const assignTemplate = createAsyncThunk<
  AssignTemplateResponse,
  {
    selectedTemplate: any;
    selectedEmployees: any[];
    editedFields: any[];
    selectedProjectId: string;
    selectedServiceTitle: string;
  },
  { state: RootState; rejectValue: string }
>('documentTemplate/assignTemplate', async (payload, { rejectWithValue }) => {
  try {
    const {
      selectedTemplate,
      selectedEmployees,
      editedFields,
      selectedProjectId,
      selectedServiceTitle
    } = payload;
    const processedFields = editedFields.map((field) => ({
      ...field,
      isEmpEdit: !field.value || field.value === ''
    }));
    const assignmentPromises = selectedEmployees.map(async (employee) => {
      const assignmentPayload: AssignTemplatePayload = {
        group_id: selectedProjectId,
        service_id: selectedServiceTitle,
        documentId: selectedTemplate.documentId,
        documentName: selectedTemplate.documentName,
        documentDate: new Date().toISOString().split('T')[0],
        category: selectedTemplate.category,
        subCategory: selectedTemplate.subCategory,
        categoryId: selectedTemplate.categoryId,
        subcategoryId: selectedTemplate.subcategoryId,
        inputFields: processedFields,
        uploadedFile: selectedTemplate.uploadedFile,
        assignedEmployeeMailId: employee.email,
        assignedEmpName: employee.name,
        avatar: employee.avatar
      };
      const response = await axiosInstance.post(
        '/people-screening/doc/assigned-template',
        assignmentPayload
      );
      return response.data;
    });
    await Promise.all(assignmentPromises);
    return {
      message: `Template "${selectedTemplate.documentName}" successfully assigned to ${selectedEmployees.length} employee(s).`,
      success: true
    };
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || 'Failed to assign template. Please try again.'
    );
  }
});

export const submitESignDoc = createAsyncThunk<
  SubmitESignDocResponse,
  SubmitESignDocPayload,
  { state: RootState; rejectValue: string }
>('documentTemplate/submitESignDoc', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/people-screening/doc/submit-esign-doc', payload);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to submit e-sign document');
  }
});

export const createTemplate = createAsyncThunk<
  Template,
  CreateTemplatePayload,
  { state: RootState; rejectValue: string }
>('documentTemplate/createTemplate', async (payload, { rejectWithValue }) => {
  console.log('Creating template with payload:', payload);
  try {
    const response = await axiosInstance.post('/people-screening/doc/create-template', payload);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create template');
  }
});

export const listTemplates = createAsyncThunk<
  ListTemplatesResponse,
  { group_id: string; service_id: string; category_id: string | null },
  { state: RootState; rejectValue: string }
>('documentTemplate/listTemplates', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/people-screening/doc/list-templates', payload);
    console.log('List Templates Response:', response.data);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch templates');
  }
});

export const getTemplateById = createAsyncThunk<
  Template,
  GetTemplateByIdPayload,
  { state: RootState; rejectValue: string }
>('documentTemplate/getTemplateById', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/people-screening/doc/get-template-by-id', payload);
    return response.data.template;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch template');
  }
});

export const updateTemplateById = createAsyncThunk<
  Template,
  UpdateTemplateByIdPayload,
  { state: RootState; rejectValue: string }
>('documentTemplate/updateTemplateById', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/people-screening/doc/update-template-by-id',
      payload
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update template');
  }
});

export const deleteTemplateById = createAsyncThunk<
  { documentId: string },
  DeleteTemplateByIdPayload,
  { state: RootState; rejectValue: string }
>('documentTemplate/deleteTemplateById', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/people-screening/doc/delete-template-by-id',
      payload
    );
    return { documentId: payload.document_id };
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete template');
  }
});

export const getAllAssignedDocuments = createAsyncThunk<
  GetAllAssignedDocumentsResponse,
  GetAllAssignedDocumentsPayload,
  { state: RootState; rejectValue: string }
>('documentTemplate/getAllAssignedDocuments', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/people-screening/doc/get-all-assigned-doc',
      payload
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch assigned documents');
  }
});

// Define action type for createAsyncThunk to include meta
interface AsyncThunkAction<T, P> extends Action {
  payload: T;
  meta: {
    arg: P;
    requestId: string;
    requestStatus: 'pending' | 'fulfilled' | 'rejected';
  };
}

// Redux Slice
const documentTemplateSlice = createSlice({
  name: 'documentTemplate',
  initialState,
  reducers: {
    resetTemplateState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = null;
      state.templates = [];
      state.categories = [];
      state.availableCategories = [];
      state.availableSubcategories = [];
      state.totalCategories = 0;
      state.totalSubcategories = 0;
      state.totalTemplates = 0;
      state.hasMoreTemplates = true;
      state.categoryLoading = false;
      state.categoryError = null;
      state.subcategoryLoading = false;
      state.subcategoryError = null;
      state.templateCreating = false;
      state.templateFetching = false;
      state.templateUpdating = false;
      state.templateDeleting = false;
      state.templateAssigning = false;
      state.templateAssignmentError = null;
      state.templateAssignmentSuccess = false;
      state.templateAssignmentMessage = null;
      state.selectedTemplate = null;
      state.assignedDocumentsLoading = false;
      state.assignedDocumentsError = null;
      state.assignedDocumentCategories = [];
      state.totalAssignedDocumentCategories = 0;
      state.totalAssignedDocuments = 0;
      state.selectedAssignedDocument = null;
      state.acceptDeleteReassignLoading = false;
      state.acceptDeleteReassignError = null;
      state.acceptDeleteReassignSuccess = false;
      state.acceptDeleteReassignMessage = null;
    },
    clearTemplateError: (state) => {
      state.error = null;
    },
    clearTemplateSuccessMessage: (state) => {
      state.success = false;
      state.message = null;
    },
    clearCategoryError: (state) => {
      state.categoryError = null;
    },
    clearSubcategoryError: (state) => {
      state.subcategoryError = null;
    },
    clearAssignedDocumentsError: (state) => {
      state.assignedDocumentsError = null;
    },
    clearTemplateAssignmentError: (state) => {
      state.templateAssignmentError = null;
    },
    clearTemplateAssignmentSuccess: (state) => {
      state.templateAssignmentSuccess = false;
      state.templateAssignmentMessage = null;
    },
    clearAcceptDeleteReassignError: (state) => {
      state.acceptDeleteReassignError = null;
    },
    clearAcceptDeleteReassignSuccess: (state) => {
      state.acceptDeleteReassignSuccess = false;
      state.acceptDeleteReassignMessage = null;
    },
    setSelectedTemplate: (state, action: PayloadAction<Template | null>) => {
      state.selectedTemplate = action.payload;
    },
    setSelectedAssignedDocument: (state, action: PayloadAction<AssignedDocument | null>) => {
      state.selectedAssignedDocument = action.payload;
    }
  },
  extraReducers: (builder) => {
    // CRUD Categories
    builder
      .addCase(crudCategories.pending, (state) => {
        state.categoryLoading = true;
        state.categoryError = null;
        state.success = false;
        state.message = null;
      })
      .addCase(crudCategories.fulfilled, (state, action) => {
        state.categoryLoading = false;
        state.categoryError = null;
        if (action.payload.message) {
          state.message = action.payload.message;
        }

        const inputPayload = action.meta.arg as CrudCategoryPayload;

        if (inputPayload.action === 'get' && action.payload.categories) {
          // API returns Category objects directly
          state.availableCategories = action.payload.categories;
          state.totalCategories =
            action.payload.total_categories || action.payload.categories.length;
        } else if (
          inputPayload.action === 'create' &&
          action.payload.category_id &&
          inputPayload.category
        ) {
          // Check if category already exists
          const categoryExists = state.availableCategories.some(
            (cat) => cat.category === inputPayload.category
          );
          if (!categoryExists) {
            // Create new category object - this should match the Category interface exactly
            const newCategory: Category = {
              category_id: action.payload.category_id,
              category: inputPayload.category,
              createdDate: new Date().toISOString(),
              createdBy: 'current-user',
              status: 'active',
              lastModified: new Date().toISOString(),
              lastModifiedBy: 'current-user'
            };
            state.availableCategories.push(newCategory);
            state.totalCategories += 1;
          }
        } else if (
          inputPayload.action === 'update' &&
          action.payload.category_id &&
          inputPayload.category
        ) {
          // Find and update category
          const index = state.availableCategories.findIndex(
            (cat) => cat.category_id === action.payload.category_id
          );
          if (index !== -1) {
            const oldCategoryName = state.availableCategories[index].category;
            state.availableCategories[index].category = inputPayload.category;
            state.availableCategories[index].lastModified = new Date().toISOString();
            state.availableCategories[index].lastModifiedBy = 'current-user';

            // Update category name in templates and categories arrays
            state.categories.forEach((category) => {
              if (category.category === oldCategoryName) {
                category.category = inputPayload.category || '';
              }
            });
            state.templates.forEach((template) => {
              if (template.category === oldCategoryName) {
                template.category = inputPayload.category || '';
              }
            });
          }
        } else if (inputPayload.action === 'delete' && action.payload.category_id) {
          // Find the category name before deletion
          const deletedCategory = state.availableCategories.find(
            (cat) => cat.category_id === action.payload.category_id
          );
          const deletedCategoryName = deletedCategory?.category;

          // Remove from availableCategories
          state.availableCategories = state.availableCategories.filter(
            (cat) => cat.category_id !== action.payload.category_id
          );

          // Clean up templates and categories arrays
          if (deletedCategoryName) {
            state.categories = state.categories.filter(
              (cat) => cat.category !== deletedCategoryName
            );
            // You might also want to handle templates with this category
            state.templates = state.templates.filter(
              (template) => template.category !== deletedCategoryName
            );
          }

          state.totalCategories = Math.max(0, state.totalCategories - 1);
        }

        state.success = true;
      })
      .addCase(crudCategories.rejected, (state, action) => {
        state.categoryLoading = false;
        state.categoryError = action.payload || 'Failed to perform category operation';
        state.success = false;
      });

    // CRUD Subcategories
    // Update the CRUD Subcategories section in your Redux slice
    builder
      .addCase(crudSubcategories.pending, (state) => {
        state.subcategoryLoading = true;
        state.subcategoryError = null;
        state.success = false;
        state.message = null;
      })
      .addCase(crudSubcategories.fulfilled, (state, action) => {
        state.subcategoryLoading = false;
        state.subcategoryError = null;
        if (action.payload.message) {
          state.message = action.payload.message;
        }

        const inputPayload = action.meta.arg as CrudSubcategoryPayload;

        if (inputPayload.action === 'get' && action.payload.subcategories) {
          state.availableSubcategories = action.payload.subcategories;
          state.totalSubcategories =
            action.payload.total_subcategories || action.payload.subcategories.length;
        } else if (
          inputPayload.action === 'create' &&
          action.payload.subcategory_id &&
          inputPayload.subcategory
        ) {
          // Check if subcategory already exists
          const subcategoryExists = state.availableSubcategories.some(
            (subcat) => subcat.subcategory === inputPayload.subcategory
          );
          if (!subcategoryExists) {
            const newSubcategory: Subcategory = {
              subcategory_id: action.payload.subcategory_id,
              subcategory: inputPayload.subcategory,
              createdDate: new Date().toISOString(),
              createdBy: 'current-user',
              status: 'active',
              lastModified: new Date().toISOString(),
              lastModifiedBy: 'current-user'
            };
            state.availableSubcategories.push(newSubcategory);
            state.totalSubcategories += 1;
          }
        } else if (
          inputPayload.action === 'update' &&
          action.payload.subcategory_id &&
          inputPayload.subcategory
        ) {
          const index = state.availableSubcategories.findIndex(
            (subcat) => subcat.subcategory_id === action.payload.subcategory_id
          );
          if (index !== -1) {
            const oldSubcategory = state.availableSubcategories[index].subcategory;
            state.availableSubcategories[index].subcategory = inputPayload.subcategory;
            state.availableSubcategories[index].lastModified = new Date().toISOString();
            state.availableSubcategories[index].lastModifiedBy = 'current-user';

            // Update subcategory in templates
            state.templates.forEach((template) => {
              if (template.subCategory === oldSubcategory) {
                template.subCategory = inputPayload.subcategory;
              }
            });
          }
        } else if (inputPayload.action === 'delete' && action.payload.subcategory_id) {
          const deletedSubcategory = state.availableSubcategories.find(
            (subcat) => subcat.subcategory_id === action.payload.subcategory_id
          );

          state.availableSubcategories = state.availableSubcategories.filter(
            (subcat) => subcat.subcategory_id !== action.payload.subcategory_id
          );

          // Clear subcategory from templates
          if (deletedSubcategory) {
            state.templates.forEach((template) => {
              if (template.subCategory === deletedSubcategory.subcategory) {
                template.subCategory = undefined;
              }
            });
          }

          state.totalSubcategories = Math.max(0, state.totalSubcategories - 1);
        }

        state.success = true;
      })
      .addCase(crudSubcategories.rejected, (state, action) => {
        state.subcategoryLoading = false;
        state.subcategoryError = action.payload || 'Failed to perform subcategory operation';
        state.success = false;
      });

    // Accept/Delete/Reassign Document
    builder
      .addCase(acceptDeleteReassignDoc.pending, (state) => {
        state.acceptDeleteReassignLoading = true;
        state.acceptDeleteReassignError = null;
        state.acceptDeleteReassignSuccess = false;
        state.acceptDeleteReassignMessage = null;
      })
      .addCase(
        acceptDeleteReassignDoc.fulfilled,
        (state, action: PayloadAction<AcceptDeleteReassignDocResponse>) => {
          state.acceptDeleteReassignLoading = false;
          state.acceptDeleteReassignSuccess = action.payload.success;
          state.acceptDeleteReassignMessage = action.payload.message;
        }
      )
      .addCase(
        acceptDeleteReassignDoc.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.acceptDeleteReassignLoading = false;
          state.acceptDeleteReassignError = action.payload || 'Failed to process document action';
          state.acceptDeleteReassignSuccess = false;
        }
      );

    // Assign Template
    builder
      .addCase(assignTemplate.pending, (state) => {
        state.templateAssigning = true;
        state.templateAssignmentError = null;
        state.templateAssignmentSuccess = false;
        state.templateAssignmentMessage = null;
      })
      .addCase(assignTemplate.fulfilled, (state, action: PayloadAction<AssignTemplateResponse>) => {
        state.templateAssigning = false;
        state.templateAssignmentSuccess = true;
        state.templateAssignmentMessage = action.payload.message;
        state.templateAssignmentError = null;
      })
      .addCase(assignTemplate.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.templateAssigning = false;
        state.templateAssignmentError = action.payload || 'Failed to assign template';
        state.templateAssignmentSuccess = false;
      });

    // Submit E-Sign Document
    builder
      .addCase(submitESignDoc.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      .addCase(submitESignDoc.fulfilled, (state, action: PayloadAction<SubmitESignDocResponse>) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(submitESignDoc.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to submit e-sign document';
        state.success = false;
      });

    // Create Template
    builder
      .addCase(createTemplate.pending, (state) => {
        state.templateCreating = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      // Create Template - Fixed
      .addCase(createTemplate.fulfilled, (state, action: PayloadAction<Template>) => {
        state.templateCreating = false;
        state.success = true;
        state.message = 'Template created successfully';
        state.templates.push(action.payload);
        state.totalTemplates += 1;
        const categoryExists = state.categories.find(
          (cat) => cat.category === action.payload.category
        );
        if (categoryExists) {
          // Fix: Initialize templates array and count if undefined
          if (!categoryExists.templates) {
            categoryExists.templates = [];
          }
          if (categoryExists.count === undefined) {
            categoryExists.count = 0;
          }
          categoryExists.templates.push(action.payload);
          categoryExists.count += 1;
        } else {
          state.categories.push({
            category: action.payload.category,
            templates: [action.payload],
            count: 1
          });
          state.totalCategories += 1;
        }
      })

      .addCase(createTemplate.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.templateCreating = false;
        state.error = action.payload || 'Failed to create template';
        state.success = false;
      });

    // List Templates - FIXED REDUCER
    builder
      .addCase(listTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      .addCase(listTemplates.fulfilled, (state, action: PayloadAction<ListTemplatesResponse>) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;

        if (action.payload.categories) {
          // FIXED: Convert API categories to expected CategoryData format
          const processedCategories = action.payload.categories.map((category) => ({
            category: category.category,
            templates: category.subcategories
              ? category.subcategories.flatMap((subcategory) => subcategory.templates || [])
              : [],
            count: category.totalTemplates || category.total_templates || 0
          }));

          // Now assign the correctly formatted categories
          state.categories = processedCategories;

          state.totalCategories =
            action.payload.totalCategories || action.payload.total_categories || 0;
          state.totalTemplates =
            action.payload.totalTemplates || action.payload.total_templates || 0;

          // Flatten templates from nested structure
          const flattenedTemplates: Template[] = [];

          action.payload.categories.forEach((category: any) => {
            if (category.subcategories && Array.isArray(category.subcategories)) {
              category.subcategories.forEach((subcategory: any) => {
                if (subcategory.templates && Array.isArray(subcategory.templates)) {
                  const templatesWithCategory = subcategory.templates.map((template: any) => ({
                    ...template,
                    category: category.category || template.category,
                    subcategory: subcategory.subcategory || template.subcategory
                  }));
                  flattenedTemplates.push(...templatesWithCategory);
                }
              });
            }
          });

          state.templates = flattenedTemplates;
          state.hasMoreTemplates = flattenedTemplates.length > 0;

          console.log('Redux: Processed categories:', state.categories.length);
          console.log('Redux: Flattened templates:', flattenedTemplates.length);
        }
        // Handle backward compatibility for old API response format
        else if (action.payload.templates && action.payload.category) {
          const categoryExists = state.categories.find(
            (cat) => cat.category === action.payload.category
          );
          if (categoryExists) {
            categoryExists.templates = action.payload.templates;
            categoryExists.count = action.payload.total_count || action.payload.templates.length;
          } else {
            state.categories.push({
              category: action.payload.category,
              templates: action.payload.templates,
              count: action.payload.total_count || action.payload.templates.length
            });
            state.totalCategories += 1;
          }
          state.templates = state.categories.flatMap((cat) => cat.templates || []);
          state.totalTemplates = state.templates.length;
          state.hasMoreTemplates = action.payload.templates.length > 0;
        }
      })
      .addCase(listTemplates.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch templates';
        state.success = false;
      });

    // Get Template By ID
    builder
      .addCase(getTemplateById.pending, (state) => {
        state.templateFetching = true;
        state.error = null;
      })
      // Get Template By ID - Fixed
      .addCase(getTemplateById.fulfilled, (state, action: PayloadAction<Template>) => {
        state.templateFetching = false;
        state.selectedTemplate = action.payload;
        const templateIndex = state.templates.findIndex(
          (template) => template.documentId === action.payload.documentId
        );
        if (templateIndex !== -1) {
          state.templates[templateIndex] = action.payload;
        }
        state.categories.forEach((category) => {
          // Fix: Check if templates array exists
          if (category.templates) {
            const categoryTemplateIndex = category.templates.findIndex(
              (template) => template.documentId === action.payload.documentId
            );
            if (categoryTemplateIndex !== -1) {
              category.templates[categoryTemplateIndex] = action.payload;
            }
          }
        });
      })

      .addCase(getTemplateById.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.templateFetching = false;
        state.error = action.payload || 'Failed to fetch template details';
      });

    // Update Template By ID
    builder
      .addCase(updateTemplateById.pending, (state) => {
        state.templateUpdating = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      // Update Template By ID - Fixed
      .addCase(updateTemplateById.fulfilled, (state, action: PayloadAction<Template>) => {
        state.templateUpdating = false;
        state.success = true;
        state.message = 'Template updated successfully';
        const templateIndex = state.templates.findIndex(
          (template) => template.documentId === action.payload.documentId
        );
        if (templateIndex !== -1) {
          state.templates[templateIndex] = action.payload;
        }
        state.categories.forEach((category) => {
          // Fix: Check if templates array exists
          if (category.templates) {
            const categoryTemplateIndex = category.templates.findIndex(
              (template) => template.documentId === action.payload.documentId
            );
            if (categoryTemplateIndex !== -1) {
              category.templates[categoryTemplateIndex] = action.payload;
            }
          }
        });
        if (state.selectedTemplate?.documentId === action.payload.documentId) {
          state.selectedTemplate = action.payload;
        }
      })

      .addCase(updateTemplateById.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.templateUpdating = false;
        state.error = action.payload || 'Failed to update template';
        state.success = false;
      });

    // Delete Template By ID
    builder
      .addCase(deleteTemplateById.pending, (state) => {
        state.templateDeleting = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      // Delete Template By ID - Fixed
      .addCase(
        deleteTemplateById.fulfilled,
        (state, action: PayloadAction<{ documentId: string }>) => {
          state.templateDeleting = false;
          state.success = true;
          state.message = 'Template deleted successfully';
          state.templates = state.templates.filter(
            (template) => template.documentId !== action.payload.documentId
          );
          state.totalTemplates -= 1;

          state.categories.forEach((category) => {
            // Fix: Check if templates array exists before processing
            if (category.templates) {
              const initialLength = category.templates.length;
              category.templates = category.templates.filter(
                (template) => template.documentId !== action.payload.documentId
              );
              if (category.templates.length < initialLength) {
                // Fix: Initialize count if undefined before decrementing
                if (category.count === undefined) {
                  category.count = 0;
                }
                category.count = Math.max(0, category.count - 1);
              }
            }
          });

          if (state.selectedTemplate?.documentId === action.payload.documentId) {
            state.selectedTemplate = null;
          }

          // Fix: Filter categories that have templates and check for undefined
          state.categories = state.categories.filter(
            (category) => category.templates && category.templates.length > 0
          );
          state.totalCategories = state.categories.length;
        }
      )

      .addCase(deleteTemplateById.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.templateDeleting = false;
        state.error = action.payload || 'Failed to delete template';
        state.success = false;
      });

    // Get All Assigned Documents
    // Fix the getAllAssignedDocuments reducer
    builder
      .addCase(getAllAssignedDocuments.pending, (state) => {
        state.assignedDocumentsLoading = true;
        state.assignedDocumentsError = null;
      })
      .addCase(
        getAllAssignedDocuments.fulfilled,
        (state, action: PayloadAction<GetAllAssignedDocumentsResponse>) => {
          state.assignedDocumentsLoading = false;
          state.assignedDocumentsError = null;

          // Direct assignment since API response matches AssignedDocumentCategory[]
          state.assignedDocumentCategories = action.payload.categories;
          state.totalAssignedDocumentCategories = action.payload.totalCategories;
          state.totalAssignedDocuments = action.payload.totalDocuments;
        }
      )
      .addCase(
        getAllAssignedDocuments.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.assignedDocumentsLoading = false;
          state.assignedDocumentsError = action.payload || 'Failed to fetch assigned documents';
        }
      );
  }
});

export const {
  resetTemplateState,
  clearTemplateError,
  clearTemplateSuccessMessage,
  clearCategoryError,
  clearSubcategoryError,
  clearAssignedDocumentsError,
  clearTemplateAssignmentError,
  clearTemplateAssignmentSuccess,
  clearAcceptDeleteReassignError,
  clearAcceptDeleteReassignSuccess,
  setSelectedTemplate,
  setSelectedAssignedDocument
} = documentTemplateSlice.actions;

export default documentTemplateSlice.reducer;
